import React, { useMemo } from 'react';
import { Link } from '@remix-run/react';
import type { Dayjs } from 'dayjs';
import { DownloadIcon, FileIcon, InfoIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { Separator } from '@rocco/components/separator';
import { Spinner } from '@rocco/components/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { RICH_TEXT_COMPACT_CLASS } from '~/utils/rich-text';
import { localizeTime } from '~/modules/root';
import { useUserLoggedIn } from '~/modules/user';
import { isImageFile } from '~/modules/file/util/is-image-file';
import type { EnquiryProfessional, Message } from '../../types';
import { formatDateFull, formatDatePartial, linkifyText } from '../../utils';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

const TOOLTIP_DELAY_DURATION = 300;

/**
 * A message bubble representing a message in a conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatBoxMessageBubble({
    message,
    professional,
    hideSender = false,
}: {
    /**
     * The conversation message.
     */
    message: Message;
    /**
     * The professional of from the initial enquiry, if this is for an EnquiryConversation.
     */
    professional?: EnquiryProfessional;
    /**
     * Hide the sender name and avatar. This can be set if the previous message
     * was from the same sender. Defaults to `false`.
     */
    hideSender?: boolean;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const senderParticipant = message.Participant;
    const sender = senderParticipant.Identity;
    const isOutgoing =
        isLoggedIn && sender.__typename === 'Member' && sender.ID === user.ID;
    const senderName = useMemo(() => {
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message.CreatedBy, sender]);

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-[8px]',
                isOutgoing ? 'items-end' : 'items-start'
            )}
        >
            {!hideSender &&
                !isOutgoing &&
                message.__typename !== 'SystemMessage' &&
                message.__typename !== 'RevealContactDetailsMessage' && (
                    <div className="flex w-full items-center gap-[8px]">
                        <ParticipantAvatar
                            className="size-[28px]"
                            fallbackClassName="!text-[13px]"
                            participant={
                                // Show the professional avatar if the sender is a collaborator.
                                senderParticipant.__typename ===
                                    'EnquiryParticipant' &&
                                senderParticipant.IsCollaborator &&
                                professional
                                    ? professional
                                    : sender
                            }
                        />
                        <span className="min-w-0 flex-1 break-words text-[12px] font-medium leading-tight">
                            {senderName}
                        </span>
                    </div>
                )}
            {message.__typename === 'TextMessage' && (
                <TextMessageBubble
                    content={message.Content}
                    isHTML={message.IsHTML}
                    createdAt={localizeTime(message.Created)}
                    isOptimistic={message.isOptimistic ?? false}
                    isOutgoing={isOutgoing}
                />
            )}
            {message.__typename === 'FileAttachmentMessage' && (
                <FileAttachmentMessageBubble
                    content={message.Content}
                    attachments={message.Attachments}
                    isHTML={message.IsHTML}
                    createdAt={localizeTime(message.Created)}
                    isOptimistic={message.isOptimistic ?? false}
                    isOutgoing={isOutgoing}
                />
            )}
            {message.__typename === 'SystemMessage' && (
                <SystemMessageBubble message={message} />
            )}
            {message.__typename === 'MemberEnquiryMessage' && (
                <MemberEnquiryMessageBubble
                    message={message}
                    isOutgoing={isOutgoing}
                />
            )}
            {message.__typename === 'RevealContactDetailsMessage' && (
                <RevealContactDetailsMessageBubble message={message} />
            )}
        </div>
    );
}

function TextMessageBubble({
    content,
    isHTML,
    createdAt,
    isOptimistic,
    isOutgoing,
}: {
    content: string;
    isHTML: boolean;
    createdAt: Dayjs;
    isOptimistic: boolean;
    isOutgoing: boolean;
}) {
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const linkifiedContent = useMemo(() => {
        return isHTML
            ? [content]
            : linkifyText(content, 'text-black underline font-medium');
    }, [content, isHTML]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className={cn(
                            'w-fit min-w-[40%] max-w-[80%] space-y-[2px] break-words rounded-[8px] p-[8px] text-[12px] leading-normal tracking-wide',
                            isOutgoing
                                ? 'bg-black/30 text-white'
                                : 'bg-white text-black'
                        )}
                    >
                        {isHTML ? (
                            <div
                                className={cn(
                                    RICH_TEXT_COMPACT_CLASS,
                                    'whitespace-pre-wrap'
                                )}
                                dangerouslySetInnerHTML={{ __html: content }}
                            ></div>
                        ) : (
                            <div className="whitespace-pre-wrap">
                                {linkifiedContent}
                            </div>
                        )}
                        <div className="flex items-center justify-end gap-[4px] text-[10px] opacity-70">
                            <time
                                className="flex-1 truncate text-right"
                                dateTime={a11yDateString}
                            >
                                {createdAtString}
                            </time>
                            {isOptimistic && (
                                <Spinner className="size-[12px] shrink-0" />
                            )}
                        </div>
                    </div>
                </TooltipTrigger>
                <TooltipContent side={isOutgoing ? 'left' : 'right'}>
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function FileAttachmentMessageBubble({
    content,
    attachments,
    isHTML,
    createdAt,
    isOptimistic,
    isOutgoing,
}: {
    content?: string | null;
    attachments: Extract<
        Message,
        { __typename: 'FileAttachmentMessage' }
    >['Attachments'];
    isHTML: boolean;
    createdAt: Dayjs;
    isOptimistic: boolean;
    isOutgoing: boolean;
}) {
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);

    return (
        <>
            {!!content && (
                <TextMessageBubble
                    content={content}
                    isHTML={isHTML}
                    createdAt={createdAt}
                    isOptimistic={isOptimistic}
                    isOutgoing={isOutgoing}
                />
            )}
            {!!attachments.length && (
                <div
                    className={cn(
                        'flex w-full gap-[8px]',
                        isOutgoing ? 'flex-wrap justify-end' : 'flex-col'
                    )}
                >
                    {attachments.map(({ ID, Link, FileName }) => (
                        <TooltipProvider
                            key={ID}
                            delayDuration={TOOLTIP_DELAY_DURATION}
                        >
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={cn(
                                            'relative overflow-hidden rounded-[8px] text-[12px] leading-normal tracking-normal',
                                            isOutgoing
                                                ? 'w-fit bg-black/30 text-white'
                                                : 'w-3/5 bg-white text-black'
                                        )}
                                    >
                                        <a
                                            className={cn(
                                                'flex items-center gap-[8px] p-[8px]',
                                                isOutgoing
                                                    ? 'hover:bg-black/30'
                                                    : 'hover:bg-black/20'
                                            )}
                                            href={Link}
                                            target="_blank"
                                            rel="noopener"
                                            download
                                        >
                                            {!isOutgoing &&
                                            isImageFile(FileName) ? (
                                                <img
                                                    className="size-[48px] shrink-0 object-cover"
                                                    src={Link}
                                                    alt={FileName ?? ''}
                                                />
                                            ) : (
                                                <FileIcon
                                                    className={cn(
                                                        'shrink-0',
                                                        isOutgoing
                                                            ? 'size-[24px]'
                                                            : 'size-[48px] p-[12px]'
                                                    )}
                                                />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="line-clamp-2 break-words font-medium">
                                                    {FileName}
                                                </div>
                                                {!isOutgoing && (
                                                    <div className="flex items-center gap-[4px] opacity-70">
                                                        <DownloadIcon className="size-[12px]" />
                                                        <span>Download</span>
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                        {(isOptimistic || !Link) &&
                                            isOutgoing && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                                    <Spinner className="size-[20px]" />
                                                </div>
                                            )}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent
                                    side={isOutgoing ? 'top' : 'right'}
                                >
                                    {tooltipDateString}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ))}
                </div>
            )}
        </>
    );
}

function SystemMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'SystemMessage' }>;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const senderName = useMemo(() => {
        const sender = message.Participant.Identity;
        if (sender.__typename === 'Professional') {
            return message.CreatedBy
                ? `${message.CreatedBy} (${sender.Title})`
                : sender.Title;
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        }
        return sender.Name;
    }, [message]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full break-words rounded-[8px] bg-black/25 p-[8px] text-[12px] leading-normal tracking-wide text-white">
                        {`${senderName} updated the Conversation Settings`}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function MemberEnquiryMessageBubble({
    message,
    isOutgoing,
}: {
    message: Extract<Message, { __typename: 'MemberEnquiryMessage' }>;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const items = useMemo(() => {
        return message.LibraryItems.filter(
            (item) => item.__typename !== 'Professional'
        );
    }, [message.LibraryItems]);

    // TODO: Tracker: CommentLibraryItems.tsx

    return items.length ? (
        <>
            <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className={cn(
                                'w-fit min-w-[60%] max-w-[80%] overflow-hidden break-words rounded-[8px] text-[12px] leading-normal tracking-wide',
                                isOutgoing
                                    ? 'bg-black/30 text-white'
                                    : 'bg-white text-black'
                            )}
                        >
                            {items.map(
                                ({
                                    ID,
                                    Title,
                                    Link: link,
                                    Professional,
                                    LibraryThumb,
                                }) => (
                                    <React.Fragment key={ID}>
                                        <Link
                                            to={link}
                                            className={cn(
                                                'block w-full p-[8px] leading-tight',
                                                isOutgoing
                                                    ? 'hover:bg-black/30'
                                                    : 'hover:bg-black/20'
                                            )}
                                        >
                                            {!!LibraryThumb?.ThumbImageFile
                                                ?.Thumbnail.Desktop && (
                                                <Image
                                                    className="mb-[12px] w-[140px]"
                                                    src={
                                                        LibraryThumb
                                                            .ThumbImageFile
                                                            .Thumbnail.Desktop
                                                    }
                                                    alt={Title}
                                                    fit="contain"
                                                    fadeIn={false}
                                                />
                                            )}
                                            <div className="mb-[2px] font-medium">
                                                {Title}
                                            </div>
                                            <div className="opacity-70">
                                                {Professional?.Title}
                                            </div>
                                        </Link>
                                        <div className="px-[8px]">
                                            <Separator
                                                className={cn(
                                                    isOutgoing
                                                        ? 'bg-white/20'
                                                        : 'bg-border'
                                                )}
                                            />
                                        </div>
                                    </React.Fragment>
                                )
                            )}
                            <div className="space-y-[2px] p-[8px]">
                                <div
                                    className={cn(
                                        RICH_TEXT_COMPACT_CLASS,
                                        'whitespace-pre-wrap'
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: message.Content,
                                    }}
                                ></div>
                                <time
                                    className="block flex-1 truncate text-right text-[10px] opacity-70"
                                    dateTime={a11yDateString}
                                >
                                    {createdAtString}
                                </time>
                            </div>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent side={isOutgoing ? 'left' : 'right'}>
                        {tooltipDateString}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            {message.Attachments.length > 0 && (
                <FileAttachmentMessageBubble
                    content={undefined}
                    attachments={message.Attachments}
                    isHTML
                    createdAt={createdAt}
                    isOptimistic={message.isOptimistic ?? false}
                    isOutgoing={isOutgoing}
                />
            )}
        </>
    ) : (
        <FileAttachmentMessageBubble
            content={message.Content}
            attachments={message.Attachments}
            isHTML
            createdAt={createdAt}
            isOptimistic={message.isOptimistic ?? false}
            isOutgoing={isOutgoing}
        />
    );
}

function RevealContactDetailsMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'RevealContactDetailsMessage' }>;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const tooltipDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-full space-y-[2px] rounded-[8px] bg-white p-[8px] leading-normal tracking-wide text-black">
                        <div className="flex items-center gap-[8px] break-words">
                            <InfoIcon className="size-[16px]" />
                            <div className="min-w-0 flex-1 text-[12px]">
                                {message.Content}
                            </div>
                        </div>
                        <time
                            className="block truncate text-right text-[10px] opacity-70"
                            dateTime={a11yDateString}
                        >
                            {createdAtString}
                        </time>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                    {tooltipDateString}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
